import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import "react-phone-number-input/style.css";
import {
  Content,
  Header,
  Heading,
  Main,
  SubHeading,
} from "../commons/Template/Template";
import { Form, FormLoader } from "../commons/Form/Form";
import api from "../../api/resources";
import authAPI from "../../api/authentication";
import { mapAuthErrors } from "../../api/authentication";
import routes from "../../routes/routes";
import { useModalDispatch } from "../commons/Modal/ModalContext";
import { useToastsDispatch } from "../commons/Toasts/ToastsContext";

export function SignUpRedirect() {
  let [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const checkKey = async () => {
    const searchParamsObj = Object.fromEntries(searchParams);
    const key = searchParamsObj["key"];

    if (key) {
      navigate(routes.Account.SignUpInvite, {
        state: { key: key },
        replace: true,
      });
    } else {
      navigate(routes.Account.SignUpDirect);
    }
  };

  useEffect(() => {
    checkKey();
  }, []);

  return <></>;
}

// Sign-up page
export default function SignUp() {
  // Translation and page title
  const { t } = useTranslation(["common", "account"]);
  document.title = t("account:signup.title");

  const dispatchModal = useModalDispatch();

  const [dispUser, setDispUser] = useState(null);
  const handleDispUserChange = (key, value) => {
    setDispUser((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Component pivot
  const [confirm, setConfirm] = useState(false);

  // Get e-mail address from key
  const [invitationEmail, setInvitationEmail] = useState("");
  const [loadingInvitation, setLoadingInvitation] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const checkInvitation = async () => {
    const key = location.state?.key;

    // No key: redirect to correct sign-up page
    if (!key) {
      setLoadingInvitation(false);
      navigate(routes.Account.SignUpDirect, { replace: true });
      return;
    }

    const response = await api.getInvitation(key);
    const invitation = response.ok ? await response.json() : null;

    if (invitation) {
      setInvitationEmail(invitation.email);
    } else {
      // Invitation expired (or invalid)
      dispatchModal({
        type: "add",
        title: t("account:signup.expiredInviteModal.title"),
        message: t("account:signup.expiredInviteModal.message"),
      });
    }
    setLoadingInvitation(false);
  };

  useEffect(() => {
    checkInvitation();
  }, []);

  if (loadingInvitation) return <></>;
  return (
    <Main>
      <Header>
        <Heading>
          {!confirm ? (
            dispUser && (dispUser.first_name || dispUser.last_name) ? (
              `${dispUser.first_name || ""} ${dispUser.last_name || ""}`
            ) : (
              <Trans
                t={t}
                i18nKey={`account:signup.${
                  invitationEmail ? "invitation" : "direct"
                }.heading`}
                components={{
                  emphasis: <span />,
                }}
              />
            )
          ) : (
            <Trans
              t={t}
              i18nKey="account:signup.confirm.heading"
              components={{
                emphasis: <span />,
              }}
            />
          )}
        </Heading>
        <SubHeading>
          {!confirm ? (
            <span>
              <Trans
                t={t}
                i18nKey={`account:signup.${
                  !invitationEmail
                    ? "direct.subheading"
                    : dispUser && dispUser.first_name && dispUser.last_name
                      ? "invitation.subheading.nearlyDone"
                      : "invitation.subheading.start"
                }`}
                components={
                  !invitationEmail
                    ? {
                        anchor: <Link to={routes.Account.Login} />,
                      }
                    : null
                }
              />
            </span>
          ) : (
            <>
              <p>{t("account:signup.confirm.subheading.p1")}</p>
              <p>
                <Trans
                  t={t}
                  i18nKey="account:signup.confirm.subheading.p2"
                  components={{
                    anchor: <Link to={`mailto:${routes.EmailSupport}`} />,
                  }}
                />
              </p>
            </>
          )}
        </SubHeading>
      </Header>
      <Content>
        {loadingInvitation ? (
          <FormLoader />
        ) : (
          !confirm && (
            <SignUpForm
              onConfirm={() => setConfirm(true)}
              invitationEmail={invitationEmail}
              onDispUserChange={handleDispUserChange}
            />
          )
        )}
      </Content>
    </Main>
  );
}

function SignUpForm({ onDispUserChange, invitationEmail, onConfirm }) {
  const dispatchToast = useToastsDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "account"]);

  const initialUser = {
    first_name: "",
    last_name: "",
    email: invitationEmail,
    telephone: "",
    password: "",
    is_reseller: null,
  };
  const [user, setUser] = useState(initialUser);

  const handleUserChange = (field, value) =>
    setUser((prev) => ({
      ...prev,
      [field]: value,
    }));

  const initialErrors = Object.keys(initialUser).reduce(
    (prev, curr) => ({
      ...prev,
      [curr]: [],
    }),
    { undefined: [] }, // global form errors (non_field_errors ?)
  );

  const [errors, setErrors] = useState(initialErrors);

  const clearErrors = () => {
    setErrors(initialErrors);
  };

  const [legalConditions, setLegalConditions] = useState(false);

  const handleRegister = async () => {
    clearErrors();
    const response = await authAPI.signUp({
      ...user,
      email: invitationEmail || user.email, // if present, use e-mail address from invitation
      is_reseller: user.is_reseller.value,
    });

    // According to status code, either navigate to next page or set errors
    switch (response.status) {
      case 200: // user successfully created
        await authAPI.signOut(); // user is automatically logged in if no verification issued
        dispatchToast({
          type: "add",
          variant: "success",
          heading: t("account:signup.messages.created.title"),
          subheading: t("account:signup.messages.created.content"),
        });
        navigate(routes.Account.Login);
        break;
      case 201: // e-mail confirmation sent
        onConfirm();
        break;
      case 400: {
        // Errors in sign-up form
        const jsonErrors = await response.json();
        setErrors(mapAuthErrors(jsonErrors.errors));
        break;
      }
      case 409: // user already exists
        dispatchToast({
          type: "add",
          variant: "info",
          heading: t("account:signup.messages.existing.title"),
          subheading: t("account:signup.messages.existing.content"),
        });
        navigate(routes.Account.Login);
        break;
      default:
        console.log(response);
        break;
    }
  };

  const isResellerOptions = [
    {
      label: t("account:signup.form.reseller.option.manager"),
      value: false,
      className: "manager",
    },
    {
      label: t("account:signup.form.reseller.option.reseller"),
      value: true,
      className: "reseller",
    },
  ];

  const isResellerInput = {
    label: t("account:signup.form.reseller.label"),
    type: "select",
    name: "is_reseller",
    options: isResellerOptions,
    value: user.is_reseller,
    onChange: (value) => handleUserChange("is_reseller", value),
    required: true,
    autoSortOptionsDisabled: true,
  };

  const firstNameInput = {
    label: t("account:signup.form.firstName"),
    type: "text",
    name: "first_name",
    autoComplete: "given-name",
    value: user.first_name,
    maxLength: 50,
    onChange: (value) => {
      handleUserChange("first_name", value);
      onDispUserChange("first_name", value);
    },
    errors: errors.first_name,
  };

  const lastNameInput = {
    label: t("account:signup.form.name"),
    type: "text",
    name: "last_name",
    autoComplete: "family-name",
    value: user.last_name,
    maxLength: 50,
    onChange: (value) => {
      handleUserChange("last_name", value);
      onDispUserChange("last_name", value);
    },
    errors: errors.last_name,
  };

  const emailInput = {
    label: t("account:signup.form.email"),
    type: "email",
    name: "email",
    autoComplete: "email",
    value: user.email,
    maxLength: 320,
    disabled: invitationEmail,
    onChange: (value) => handleUserChange("email", value),
    errors: errors.email,
  };

  const telephoneInput = {
    label: t("account:signup.form.tel"),
    name: "telephone",
    type: "tel",
    value: user.telephone,
    onChange: (value) => handleUserChange("telephone", value),
    errors: errors.telephone,
  };

  const passwordInput = {
    label: t("account:signup.form.password"),
    type: "password",
    value: user.password,
    name: "password",
    autoComplete: "new-password",
    minLength: 8,
    maxLength: 50,
    onChange: (value) => handleUserChange("password", value),
    errors: errors.password,
  };

  const conditionsInput = {
    label: <LegalConditionsLabel />,
    type: "checkbox",
    name: "legal_terms",
    value: legalConditions,
    onChange: setLegalConditions,
  };

  const inputs = [
    {
      data: [firstNameInput, lastNameInput],
      display: "grid",
    },
    {
      data: [emailInput, telephoneInput],
      display: "grid",
    },
    {
      data: [passwordInput],
    },
    {
      data: [isResellerInput],
    },
    {
      data: [conditionsInput],
    },
  ];

  return (
    <Form
      inputs={inputs}
      errors={errors.undefined}
      button={t("account:signup.form.button")}
      onSubmit={handleRegister}
    />
  );
}

export function LegalConditionsLabel() {
  const { t } = useTranslation(["common", "account"]);

  return (
    <Trans
      t={t}
      i18nKey="account:signup.form.conditions"
      components={{
        anchor1: <Link to={routes.TermsOfService} target="_blank" />,
        anchor2: <Link to={routes.PrivacyPolicy} target="_blank" />,
      }}
    />
  );
}
